import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import { shouldShowApplyButton } from 'app/shared/utils/cometUtils';
import { emitTrackApplyNowClickEvent } from 'app/shared/models/Clickstream/HdpClickstreamEvents';
import FullscreenOverlay from '../../../ui/FullscreenOverlay';
import { useDetailPanelSafe } from '../../../ui/DetailPanel';
import { HaloSpokeHeader } from '../HaloSpokeHeader';
import { ApplySpoke } from './ApplySpoke';
import { ApplySpokeContent } from './ApplySpokeContent';

/**
 * Hook that provides a handler to open the apply flow from anywhere.
 *
 * Desktop (with DetailPanel): opens ApplySpokeContent in the DetailPanel.
 * Mobile / no DetailPanel: manages a FullscreenOverlay.
 *
 * The apply flow branches based on rentalApplicationStatus:
 * - Accepted: shows an interstitial linking to Zillow's application
 * - Not accepted: opens the contact form in "request to apply" mode
 */
export const useApplyAction = (listing?: ListingDetails) => {
  const detailPanel = useDetailPanelSafe();
  const [isOpen, setIsOpen] = useState(false);
  const reduxListing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);
  const resolvedListing = listing ?? reduxListing ?? undefined;

  const useOverlay = !detailPanel || detailPanel.isMobile;

  const acceptsApplications = shouldShowApplyButton(resolvedListing?.rentalApplicationStatus);
  const overlayTitle = acceptsApplications ? 'Apply now' : 'Request to apply';

  const openApply = useCallback(() => {
    if (acceptsApplications) {
      emitTrackApplyNowClickEvent({ triggerLocation: 'home_details_halo' });
    }

    if (detailPanel && !detailPanel.isMobile) {
      detailPanel.openPanel('apply-form', ApplySpokeContent, { config: { listing: resolvedListing }, stacked: true });
    } else {
      setIsOpen(true);
    }
  }, [detailPanel, resolvedListing, acceptsApplications]);

  const applyOverlay = useOverlay ? (
    <FullscreenOverlay.Root open={isOpen} onOpenChange={setIsOpen}>
      <FullscreenOverlay.Portal>
        <FullscreenOverlay.Backdrop>
          <FullscreenOverlay.Content>
            <HaloSpokeHeader title={overlayTitle} />
            <FullscreenOverlay.Body padding="edge-to-edge">
              <ApplySpoke onClose={() => setIsOpen(false)} listing={resolvedListing} />
            </FullscreenOverlay.Body>
          </FullscreenOverlay.Content>
        </FullscreenOverlay.Backdrop>
      </FullscreenOverlay.Portal>
    </FullscreenOverlay.Root>
  ) : null;

  return { openApply, applyOverlay };
};
